// src/pages/HomePage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicHeader } from '../components/layout/PublicHeader';
import { Hero } from '../components/home/Hero';
import { Features } from '../components/home/Features';
import { GameInfo } from '../components/home/GameInfo';
import { CallToAction } from '../components/home/CallToAction';
import { DatabaseUpdates } from '../components/home/DatabaseUpdates';
import { LoginModal } from '../components/auth/LoginModal';
import { RegisterModal } from '../components/auth/RegisterModal';
import '../styles/landing/landing.css';

export type AuthView = 'login' | 'register' | null;

const HomePage: React.FC = () => {
    // The page owns the auth modals rather than the header and the call to
    // action owning one each. That way there is a single instance of each, and
    // the two can hand off to one another without closing and reopening.
    const [authView, setAuthView] = useState<AuthView>(null);
    const navigate = useNavigate();

    const closeAuth = useCallback(() => setAuthView(null), []);
    const openLogin = useCallback(() => setAuthView('login'), []);
    const openRegister = useCallback(() => setAuthView('register'), []);

    const handleAuthSuccess = useCallback(() => {
        setAuthView(null);
        navigate('/worlds');
    }, [navigate]);

    // Stop the page scrolling underneath an open modal.
    useEffect(() => {
        if (!authView) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [authView]);

    return (
        <div className="lp">
            <PublicHeader onLogin={openLogin} onRegister={openRegister} />

            <main>
                <Hero onStart={openRegister} onLogin={openLogin} />
                <Features />
                <GameInfo />
                <CallToAction onStart={openRegister} />
                <DatabaseUpdates />
            </main>

            <footer className="lp-footer">
                <div className="lp-shell lp-footer__inner">
                    <div className="lp-footer__mark">Võmm<span>.</span>ee</div>
                    <p className="lp-footer__meta">
                        Eesti politsei rollimäng
                    </p>
                </div>
            </footer>

            <LoginModal
                isOpen={authView === 'login'}
                onClose={closeAuth}
                onSuccess={handleAuthSuccess}
                onSwitchToRegister={openRegister}
            />

            <RegisterModal
                isOpen={authView === 'register'}
                onClose={closeAuth}
                onSuccess={handleAuthSuccess}
                onSwitchToLogin={openLogin}
            />
        </div>
    );
};

export default HomePage;
